import { BusinessUnitsTableResult } from 'src/apps/dashboard/services/queries/business-units-table/helpers/transformConsolidatedResults';
import { IBusinessUnitRow } from 'src/apps/dashboard/types/BusinessUnitsTableTypes';
import { BusinessUnit } from 'src/apps/dashboard/types/DashboardTypes';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { LabelsExcludedFromDashboard } from 'src/common/types/ConfigurationTypes';

import {
	MobileBusinessUnitsTableDataQuery,
	MobileBusinessUnitsTableDataQueryVariables
} from '../MobileBusinessUnitsTable.generated';

import { buildMobileBaseVariables } from './mobileFilterVariableBuilder';

// Score is not yet supported for mobile business units — using a zero placeholder.
const DEFAULT_SCORE = { value: 0, percentage: 0 };

// The mobile GQL API returns unitId as a [key, value] tuple inside the custom_fields
// 2D array. We locate the tuple whose first element matches 'unitId' and return the second.
const extractUnitIdFromCustomFields = (
	customFields: Array<Array<string | null>>
): string | null => {
	const unitField = customFields.find((field) => field[0]?.toLowerCase() === 'unitid');
	return unitField?.[1] ?? null;
};

const aggregateRunsByUnit = (
	runs:
		| Array<{
				custom_fields: Array<Array<string | null>>;
				critical_issues_sum: number;
				serious_issues_sum: number;
				total_issues_sum: number;
		  } | null>
		| null
		| undefined
): Map<string, { critical: number; serious: number; total: number }> => {
	const unitMap = new Map<string, { critical: number; serious: number; total: number }>();

	(runs ?? []).forEach((run) => {
		if (!run) {
			return;
		}

		const unitId = extractUnitIdFromCustomFields(run.custom_fields);
		if (!unitId) {
			return;
		}

		const existing = unitMap.get(unitId) ?? { critical: 0, serious: 0, total: 0 };
		unitMap.set(unitId, {
			critical: existing.critical + (run.critical_issues_sum ?? 0),
			serious: existing.serious + (run.serious_issues_sum ?? 0),
			total: existing.total + (run.total_issues_sum ?? 0)
		});
	});

	return unitMap;
};

export const buildMobileBusinessUnitsTableVariables = (
	days: number,
	filters?: IFilterOptions,
	labelsExcludedFromDashboard?: LabelsExcludedFromDashboard
): MobileBusinessUnitsTableDataQueryVariables => ({
	days,
	...buildMobileBaseVariables(filters, labelsExcludedFromDashboard)
});

export const transformMobileBusinessUnitsTableResults = (
	rawResults: MobileBusinessUnitsTableDataQuery,
	businessUnits: BusinessUnit[]
): BusinessUnitsTableResult => {
	const mfaByUnit = aggregateRunsByUnit(rawResults.mfaRuns);
	const msdkByUnit = aggregateRunsByUnit(rawResults.msdkRuns);

	const rows: IBusinessUnitRow[] = businessUnits.map((businessUnit) => {
		const mfaMetrics = mfaByUnit.get(businessUnit.id) ?? { critical: 0, serious: 0, total: 0 };
		const msdkMetrics = msdkByUnit.get(businessUnit.id) ?? { critical: 0, serious: 0, total: 0 };

		return {
			id: businessUnit.id,
			name: businessUnit.name,
			criticalIssues: mfaMetrics.critical + msdkMetrics.critical,
			seriousIssues: mfaMetrics.serious + msdkMetrics.serious,
			uniqueIssues: mfaMetrics.total + msdkMetrics.total,
			score: DEFAULT_SCORE,
			scoreValue: 0
		};
	});

	return {
		rows,
		total: rows.length,
		unfilteredTotal: rows.length,
		scoreWebProducts: [],
		aggregatedScoreWebProducts: [],
		aggregatedScoreDeltas: []
	};
};
